import { useState, useRef, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";

import {
    useAdminDocuments,
    useCreateDocument,
    useUpdateDocument,
    useDeleteDocument,
} from "@/services/adminDocumentService";
import type { AdminDocument } from "@/services/adminDocumentService";

import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Edit2, Plus, Trash2, FileText, Loader2, FileUp, Search, ExternalLink, Calendar } from "lucide-react";
import { getFileTypeMeta } from "@/lib/fileType";

const formSchema = z.object({
    title: z.string().min(1, "Judul wajib diisi"),
    description: z.string().optional(),
    category: z.string().optional(),
    display_order: z.coerce.number().min(0).default(0),
    is_active: z.boolean().default(true),
});
type FormValues = z.infer<typeof formSchema>;

const CATEGORY_SUGGESTIONS = ["Panduan", "Formulir", "Sertifikat", "Lainnya"];

export default function DocumentsPage() {
    const { data: documents, isLoading } = useAdminDocuments();

    const createMutation = useCreateDocument();
    const updateMutation = useUpdateDocument();
    const deleteMutation = useDeleteDocument();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingDocument, setEditingDocument] = useState<AdminDocument | null>(null);

    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [alertConfig, setAlertConfig] = useState<{
        title: string;
        description: string;
        actionText: string;
        action: () => void;
        isDestructive?: boolean;
    } | null>(null);

    const [documentFile, setDocumentFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const categoryOptions = useMemo(() => {
        const unique = new Set<string>();
        (documents || []).forEach((d) => { if (d.category) unique.add(d.category); });
        return Array.from(unique).sort();
    }, [documents]);

    const filteredDocuments = (documents || []).filter((doc) =>
        (doc.title.toLowerCase().includes(search.toLowerCase()) ||
            (doc.description || "").toLowerCase().includes(search.toLowerCase()) ||
            (doc.category || "").toLowerCase().includes(search.toLowerCase())) &&
        (categoryFilter === "all" || doc.category === categoryFilter)
    );

    const totalItems = filteredDocuments.length;
    const totalPages = Math.ceil(totalItems / perPage) || 1;
    const currentPage = Math.min(page, totalPages);
    const paginatedDocuments = filteredDocuments.slice((currentPage - 1) * perPage, currentPage * perPage);
    const fromItem = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const toItem = Math.min(currentPage * perPage, totalItems);

    useEffect(() => {
        setPage(1);
    }, [search, categoryFilter]);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: "",
            description: "",
            category: "",
            display_order: 0,
            is_active: true,
        },
    });

    const handleOpenDialog = (doc?: AdminDocument) => {
        setDocumentFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";

        if (doc) {
            setEditingDocument(doc);
            form.reset({
                title: doc.title,
                description: doc.description || "",
                category: doc.category || "",
                display_order: doc.display_order,
                is_active: Boolean(doc.is_active),
            });
        } else {
            setEditingDocument(null);
            form.reset({
                title: "",
                description: "",
                category: "",
                display_order: 0,
                is_active: true,
            });
        }
        setIsDialogOpen(true);
    };

    const onSubmit = (values: FormValues) => {
        if (!editingDocument && !documentFile) {
            toast.error("File dokumen wajib diunggah");
            return;
        }

        const formData = new FormData();
        formData.append("title", values.title);
        formData.append("description", values.description || "");
        formData.append("category", values.category || "");
        formData.append("display_order", values.display_order.toString());
        formData.append("is_active", values.is_active ? "1" : "0");

        if (documentFile) {
            formData.append("file", documentFile);
        }

        if (editingDocument) {
            updateMutation.mutate(
                { id: editingDocument.id, payload: formData },
                {
                    onSuccess: () => {
                        toast.success("Dokumen berhasil diperbarui");
                        setIsDialogOpen(false);
                    },
                    onError: (err: any) => {
                        toast.error(err.response?.data?.message || "Gagal memperbarui dokumen");
                    },
                }
            );
        } else {
            createMutation.mutate(formData, {
                onSuccess: () => {
                    toast.success("Dokumen berhasil ditambahkan");
                    setIsDialogOpen(false);
                },
                onError: (err: any) => {
                    toast.error(err.response?.data?.message || "Gagal menambahkan dokumen");
                },
            });
        }
    };

    const handleDelete = (id: number) => {
        setAlertConfig({
            title: "Hapus Dokumen",
            description: "Apakah Anda yakin ingin menghapus dokumen ini? Tindakan ini tidak dapat dibatalkan.",
            actionText: "Hapus",
            isDestructive: true,
            action: () => {
                deleteMutation.mutate(id, {
                    onSuccess: () => toast.success("Dokumen berhasil dihapus"),
                    onError: (error: any) => toast.error(error?.response?.data?.message || "Gagal menghapus dokumen"),
                });
            },
        });
        setIsAlertOpen(true);
    };

    const isMutating = createMutation.isPending || updateMutation.isPending;

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <FileText className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Kelola Dokumen</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Kelola dokumen publik (panduan, formulir, sertifikat, dll) untuk peserta Konaspi.
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 px-6 font-bold shadow-md transition-all">
                        <Plus className="w-4 h-4 mr-2" /> Tambah Dokumen
                    </Button>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Cari judul, deskripsi, atau kategori..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full h-12 pl-12 pr-4 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium"
                    />
                </div>
                {categoryOptions.length > 0 && (
                    <div className="w-full sm:w-64 shrink-0">
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="w-full h-12 px-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium"
                        >
                            <option value="all">Semua Kategori</option>
                            {categoryOptions.map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                </div>
            ) : totalItems > 0 ? (
              <>
                <div className="flex flex-col gap-3">
                    {paginatedDocuments.map((doc) => {
                        const meta = getFileTypeMeta(doc.file_type);
                        const FileIcon = meta.icon;
                        return (
                            <div
                                key={doc.id}
                                className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                            >
                                <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${meta.iconWrapClass}`}>
                                    <FileIcon className="w-6 h-6" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-slate-900 dark:text-white truncate">{doc.title}</span>
                                        {!doc.is_active && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 shrink-0">Nonaktif</span>
                                        )}
                                    </div>
                                    {doc.description && (
                                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{doc.description}</p>
                                    )}
                                    <div className="flex items-center gap-2 flex-wrap mt-2">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase ${meta.badgeClass}`}>
                                            {doc.file_type || "FILE"}
                                        </span>
                                        {doc.category && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300">
                                                {doc.category}
                                            </span>
                                        )}
                                        {doc.created_at && (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(doc.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" })}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                                    <Button
                                        asChild
                                        variant="outline"
                                        size="icon"
                                        className="h-9 w-9 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg"
                                        title="Lihat file"
                                    >
                                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                            <ExternalLink className="w-4 h-4" />
                                        </a>
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-9 w-9 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg"
                                        onClick={() => handleOpenDialog(doc)}
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-9 w-9 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg"
                                        onClick={() => handleDelete(doc.id)}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 md:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                    <div className="flex items-center gap-3 text-slate-500 font-medium">
                        <span>Menampilkan {fromItem} - {toItem} dari {totalItems} data</span>
                        <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block"></div>
                        <div className="flex items-center gap-2">
                            <label htmlFor="perPage">Tampilkan:</label>
                            <select
                                id="perPage"
                                value={perPage}
                                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1 outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="rounded-lg"
                            >
                                Sebelumnya
                            </Button>
                            <span className="text-slate-500 font-medium px-1">
                                Hal {currentPage} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="rounded-lg"
                            >
                                Selanjutnya
                            </Button>
                        </div>
                    )}
                </div>
              </>
            ) : (
                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center text-slate-500">
                    Belum ada data dokumen
                </div>
            )}

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingDocument ? "Edit Dokumen" : "Tambah Dokumen Baru"}</DialogTitle>
                    </DialogHeader>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
                            <FormField
                                control={form.control}
                                name="title"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Judul <span className="text-red-500">*</span></FormLabel>
                                        <FormControl>
                                            <Input placeholder="Contoh: Call for Papers" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Deskripsi</FormLabel>
                                        <FormControl>
                                            <Textarea placeholder="Deskripsi singkat dokumen..." {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="category"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Kategori</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Contoh: Panduan" list="category-suggestions" {...field} />
                                            </FormControl>
                                            <datalist id="category-suggestions">
                                                {CATEGORY_SUGGESTIONS.map((c) => (
                                                    <option key={c} value={c} />
                                                ))}
                                            </datalist>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="display_order"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Urutan Tampil</FormLabel>
                                            <FormControl>
                                                <Input type="number" min="0" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="is_active"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between border p-4 rounded-xl shadow-sm">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-sm font-semibold">Tampilkan di Halaman Publik?</FormLabel>
                                            <div className="text-xs text-muted-foreground">Nonaktifkan untuk menyembunyikan tanpa menghapus.</div>
                                        </div>
                                        <FormControl>
                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <FormItem>
                                <FormLabel>File Dokumen {!editingDocument && <span className="text-red-500">*</span>}</FormLabel>
                                <FormControl>
                                    <div className="flex items-center gap-4">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full flex items-center gap-2 border-dashed border-2 py-8 text-gray-500 hover:text-primary hover:border-primary"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <FileUp className="w-6 h-6" />
                                            {documentFile ? documentFile.name : "Klik untuk unggah file (PDF, DOCX, dll)"}
                                        </Button>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            className="hidden"
                                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) setDocumentFile(file);
                                            }}
                                        />
                                    </div>
                                </FormControl>
                                {editingDocument?.file_url && !documentFile && (
                                    <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
                                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-md text-xs">Ada file tersimpan</span>
                                    </div>
                                )}
                            </FormItem>

                            <div className="flex gap-3 pt-4 justify-end">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-xl h-11 px-6 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                                    Batal
                                </Button>
                                <Button type="submit" disabled={isMutating} className="rounded-xl h-11 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-md">
                                    {isMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    Simpan
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
                <AlertDialogContent className="rounded-3xl border-slate-200 dark:border-slate-800 p-6 shadow-2xl max-w-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white">
                            {alertConfig?.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
                            {alertConfig?.description}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6 gap-2">
                        <AlertDialogCancel className="rounded-xl h-11 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium">Batal</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                alertConfig?.action();
                                setIsAlertOpen(false);
                            }}
                            className={`rounded-xl h-11 font-bold text-white shadow-md ${
                                alertConfig?.isDestructive
                                    ? "bg-rose-600 hover:bg-rose-700"
                                    : "bg-indigo-600 hover:bg-indigo-700"
                            }`}
                        >
                            {alertConfig?.actionText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
